import Image from "next/image";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type RefObject,
} from "react";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import {
  MasonryScroller,
  type RenderComponentProps,
  useContainerPosition,
  usePositioner,
  useResizeObserver,
} from "masonic";

import cachedTweets from "../../../../cache/tweets.json";
import Button from "@/blueprint/components/Button/Button";
import Card from "@/blueprint/components/Card/Card";
import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import IconButton from "@/blueprint/components/IconButton/IconButton";
import LiveStat from "@/blueprint/components/LiveStat/LiveStat";
import PlayIcon from "@/blueprint/components/IconButton/PlayIcon";
import SegmentedTabs from "@/blueprint/components/SegmentedTabs/SegmentedTabs";
import Text from "@/blueprint/components/Text/Text";
import VideoPlayerModal from "@/blueprint/components/VideoPlayerModal/VideoPlayerModal";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import { useAnnounce } from "@/blueprint/components/LiveAnnouncer/useAnnounce";
import {
  MotionDuration,
  MotionEasing,
  PRESS_SCALE_CLASSES,
} from "@/blueprint/tokens/Motion";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { assertNever } from "@/lib/assertNever";
import { cn } from "@/lib/utils";
import Section from "@/blueprint/components/Section/Section";
import { useTrack } from "@/analytics/events";
import {
  REVIEW_WALL_REVIEWS,
  type ReviewWallPortedCard,
  type ReviewWallTextReviewCard,
} from "@/views/shop/reviewWallReviews";
import {
  REVIEW_WALL,
  type ReviewWallCard,
  type ReviewWallTabId,
  type ReviewWallTestimonialCard,
} from "@/views/shop/shopContent";

const MEDIA_OVERLAY =
  "absolute inset-0 bg-gradient-to-b from-tint-black-70 via-transparent to-tint-black-70";

/**
 * A flat 30% black laid between the photo and the copy on every review card
 * that sets text over an image. The gradients above it shape the top and
 * bottom edges; this holds the middle, where a multi-line quote used to sit on
 * bare photo and lose its contrast.
 *
 * Pure black rather than the ink `tint-black-*` family: the tints carry a hue
 * that tilts a photo, and the neighbouring scrim is already plain black.
 */
const IMAGE_DARKEN_LAYER = "absolute inset-0 bg-black/30";

/**
 * The stat card already carries its hover: the footnote pill below steps to
 * `brand-grey` on `group-hover`, which is the card family's one-step tint
 * spent on the affordance rather than the whole surface. Tinting the card too
 * would land the pill on its own colour and erase it.
 *
 * So only the press is missing, and it comes from Button, where Figma gives On
 * Click the same colours as Hover and scale is the only thing that reads as
 * pressed. Transform alone is transitioned here — the colours belong to the
 * pill's own `transition-colors`.
 */
const STAT_BUTTON_PRESS_CLASSES = [
  "transition-transform duration-quick ease-out-strong",
  PRESS_SCALE_CLASSES,
  "motion-reduce:transition-none",
].join(" ");
const CARD_FADE_DURATION_S = MotionDuration.quickMs / 1000;
const REVIEW_TRANSITION_DURATION_S = MotionDuration.longMs / 1000;
// One load-more page is visible from the start, so the wall opens fuller and
// reaching the end takes a click less.
const INITIAL_VISIBLE_REVIEW_COUNT = 9;
const LOAD_MORE_REVIEW_COUNT = 3;
const MASONRY_COLUMN_GUTTER_PX = 24;
const SMALL_DESKTOP_BREAKPOINT_PX = 1280;
const MASONRY_ITEM_TRANSITION = {
  duration: 0.22,
  ease: "easeInOut",
} as const;
const SHORT_REVIEW_MAX_LENGTH = 90;
const MEDIUM_REVIEW_MAX_LENGTH = 300;
const TRAILING_TCO_LINKS = /(?:\s+https:\/\/t\.co\/\S+)+\s*$/u;
const TWEET_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const REVIEW_CARD_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: CARD_FADE_DURATION_S,
      ease: [...MotionEasing.inOut],
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: CARD_FADE_DURATION_S,
      ease: [...MotionEasing.inOut],
    },
  },
} satisfies Variants;

type CachedTweets = typeof import("../../../../cache/tweets.json");
type CachedTweetId = Extract<keyof CachedTweets, string>;
type CachedTweet = CachedTweets[CachedTweetId];
type CachedTweetMedia = CachedTweet["media"][number];

type ReviewWallItem =
  | {
      id: string;
      source: "existing";
      card: ReviewWallCard;
    }
  | {
      id: string;
      source: "ported";
      card: ReviewWallPortedCard;
    };

type ReviewMasonryItem = ReviewWallItem & {
  reducedMotion: boolean;
  onPlay: (card: ReviewWallTestimonialCard) => void;
};

const getReviewWallItemKey = (item: ReviewWallItem) => item.id;

const getReviewQuoteSize = (quote: string) => {
  if (quote.length <= SHORT_REVIEW_MAX_LENGTH) {
    return "text-h4";
  }
  if (quote.length <= MEDIUM_REVIEW_MAX_LENGTH) {
    return "text-h6";
  }
  return "text-paragraph-md";
};

const getTweetText = (text: string) =>
  text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(TRAILING_TCO_LINKS, "")
    .trimEnd();

const ReviewStarIcon = () => (
  <svg viewBox="0 0 29 29" fill="currentColor" className="size-[29px] shrink-0">
    <path d="m14.5 1.5 3.78 8.04L27 10.88l-6.31 6.26 1.49 8.84-7.68-4.18-7.68 4.18 1.49-8.84L2 10.88l8.72-1.34L14.5 1.5Z" />
  </svg>
);

const VerifiedBadgeIcon = () => (
  <svg
    aria-label="Verified account"
    role="img"
    viewBox="0 0 18 18"
    fill="currentColor"
    className="size-[18px] shrink-0"
  >
    <path
      fillRule="evenodd"
      d="M9 1.25 10.7 2.7l2.2-.17.72 2.08 1.9 1.14-.5 2.15.82 2.05-1.62 1.5-.2 2.2-2.18.4L10.4 15.7 8.3 15l-2.1.7-1.43-1.66-2.18-.39-.2-2.2-1.62-1.5.82-2.05-.5-2.15 1.9-1.14.72-2.08 2.2.17L9 1.25Zm3.18 5.6a.75.75 0 0 0-1.06-1.06L7.9 9 6.88 7.98a.75.75 0 1 0-1.06 1.06l1.55 1.55a.75.75 0 0 0 1.06 0l3.75-3.74Z"
      clipRule="evenodd"
    />
  </svg>
);

const HeartIcon = () => (
  <svg
    aria-hidden
    viewBox="0 0 24 24"
    fill="currentColor"
    className="size-[24px] shrink-0"
  >
    <path d="M12 21.35 10.55 20C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09A6 6 0 0 1 16.5 3C19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.51L12 21.35Z" />
  </svg>
);

type TweetAttributionProps = {
  tweet: CachedTweet;
  showAvatar?: boolean;
  inverse?: boolean;
};

const TweetAttribution = ({
  tweet,
  showAvatar = false,
  inverse = false,
}: TweetAttributionProps) => {
  const tone = inverse ? "text-content-inverse" : "text-content-primary";

  return (
    <div className={cn("flex items-start", showAvatar && "gap-20")}>
      {showAvatar && (
        <div className="size-[36px] shrink-0 overflow-hidden rounded-full bg-review-avatar-placeholder">
          <Image
            src={tweet.author.profile_image_url}
            alt=""
            width={36}
            height={36}
            sizes="36px"
            loading="lazy"
            className="size-full object-cover"
          />
        </div>
      )}
      <div
        className={cn(
          "min-w-0 font-trimMono text-review-attribution font-medium uppercase",
          tone,
        )}
      >
        <div className="flex items-center gap-[5px]">
          <span className="break-words">{tweet.author.name}</span>
          {tweet.author.verified && <VerifiedBadgeIcon />}
        </div>
        <p className="break-words">@{tweet.author.username}</p>
      </div>
    </div>
  );
};

const ReviewTextCard = ({ card }: { card: ReviewWallTextReviewCard }) => (
  <Card
    radius="m"
    className="flex min-h-full flex-col justify-between p-card-md"
  >
    <div className="flex flex-col gap-xxl">
      <div className="font-trimMono text-review-attribution font-medium uppercase">
        <p className="text-content-primary">{card.name}</p>
        <p className="text-content-secondary">
          With Matic since {card.customerSince}
        </p>
      </div>
      <blockquote
        className={cn(
          "whitespace-pre-line break-words font-ppMori font-semibold text-content-primary",
          getReviewQuoteSize(card.quote),
        )}
        style={{ textIndent: "-0.45em" }}
      >
        “{card.quote}”
      </blockquote>
    </div>
    <div className="mt-l">
      <div aria-hidden className="flex -space-x-[2px] text-functional-yellow">
        {Array.from({ length: 5 }, (_, index) => (
          <ReviewStarIcon key={index} />
        ))}
      </div>
      <span className="sr-only">Five out of five stars.</span>
    </div>
  </Card>
);

const TweetCard = ({ tweet }: { tweet: CachedTweet }) => (
  <Card
    radius="m"
    className="flex min-h-full flex-col justify-between p-card-md"
  >
    <div className="flex flex-col gap-l">
      <TweetAttribution tweet={tweet} showAvatar />
      <blockquote className="whitespace-pre-line font-ppMori text-h6 font-semibold text-content-primary">
        {getTweetText(tweet.text)}
      </blockquote>
    </div>
    <div className="mt-l flex items-center justify-between font-trimMono text-review-attribution font-medium uppercase text-content-secondary">
      <time dateTime={tweet.created_at}>
        {TWEET_DATE_FORMATTER.format(new Date(tweet.created_at))}
      </time>
      <span className="flex items-center gap-xs">
        <span className="text-functional-red">
          <HeartIcon />
        </span>
        {COMPACT_NUMBER_FORMATTER.format(tweet.public_metrics.like_count)}
      </span>
    </div>
  </Card>
);

const getTweetMediaSource = (media: CachedTweetMedia) =>
  media.type === "video"
    ? (media.preview_image_url ?? media.url)
    : (media.url ?? media.preview_image_url);

type VideoTweetCardProps = {
  tweet: CachedTweet;
  media: CachedTweetMedia;
};

const VideoTweetCard = ({ tweet, media }: VideoTweetCardProps) => {
  const mediaSource = getTweetMediaSource(media);
  const card = (
    <Card
      radius="m"
      className="relative flex flex-col justify-between bg-neutral2 p-card-md [aspect-ratio:5/6] sd:[aspect-ratio:517/662]"
    >
      {mediaSource !== undefined && (
        <Image
          src={mediaSource}
          alt={`Media from ${tweet.author.name}'s post`}
          fill
          sizes="(min-width: 1600px) 517px, (min-width: 1280px) 31vw, (min-width: 800px) 48vw, 78vw"
          loading="lazy"
          className="object-cover"
        />
      )}
      {/* Figma's 517×464 top scrim is approximated as black 60% → transparent. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[70%] bg-gradient-to-b from-black/60 to-transparent"
      />
      <div aria-hidden className={IMAGE_DARKEN_LAYER} />
      <div className="relative z-10 flex flex-col gap-l">
        <TweetAttribution tweet={tweet} inverse />
        <blockquote className="whitespace-pre-line font-ppMori text-h6 font-semibold text-content-inverse">
          {getTweetText(tweet.text)}
        </blockquote>
      </div>
      {media.type === "video" && (
        <IconButton decorative className="relative z-10 h-[56px] w-[56px]">
          <PlayIcon />
        </IconButton>
      )}
    </Card>
  );

  if (media.type !== "video") {
    return card;
  }

  return (
    <a
      href={`https://x.com/${tweet.author.username}/status/${tweet.id}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${tweet.author.name}'s video post on X`}
      className="block min-h-full"
    >
      {card}
    </a>
  );
};

const PortedWallCard = ({ card }: { card: ReviewWallPortedCard }) => {
  switch (card.kind) {
    case "review":
      return <ReviewTextCard card={card} />;

    case "tweet": {
      const tweet = cachedTweets[card.tweetId];
      const media = tweet.media[0];
      return media === undefined ? (
        <TweetCard tweet={tweet} />
      ) : (
        <VideoTweetCard tweet={tweet} media={media} />
      );
    }

    default:
      return assertNever(card);
  }
};

type WallCardProps = {
  card: ReviewWallCard;
  onPlay: (card: ReviewWallTestimonialCard) => void;
};

const WallCard = ({ card, onPlay }: WallCardProps) => {
  switch (card.kind) {
    case "press":
      return (
        <Card className="flex flex-col p-card-lg">
          <Text variant="paragraph-lg" as="blockquote">
            “{card.quote}”
          </Text>
          <Image
            src={card.logo}
            alt={card.logoAlt}
            width={180}
            height={40}
            sizes="180px"
            className="mt-auto h-8 w-auto max-w-[180px] object-contain object-left brightness-0"
          />
        </Card>
      );

    case "rating":
      return (
        <Card className="flex flex-col bg-brand-lavender p-card-lg">
          <Text variant="d1" as="p">
            {REVIEW_WALL.rating.average}
          </Text>
          <div className="mt-auto flex flex-col gap-xs">
            <div
              aria-hidden
              className="flex -space-x-[2px] text-content-primary"
            >
              {Array.from({ length: 5 }, (_, index) => (
                <ReviewStarIcon key={index} />
              ))}
            </div>
            <span className="sr-only">Five out of five stars.</span>
            <Eyebrow variant="eyebrow-sm" tone="primary">
              {REVIEW_WALL.rating.count} reviews on{" "}
              <span className="font-ppMori font-semibold normal-case">
                Shop
              </span>
            </Eyebrow>
          </div>
        </Card>
      );

    case "tweet":
      return (
        <Card className="relative [aspect-ratio:5/6]">
          <Image
            src={card.image}
            alt={card.imageAlt}
            fill
            sizes="(min-width: 1600px) 517px, (min-width: 1280px) 31vw, (min-width: 800px) 48vw, 100vw"
            className="object-cover"
          />
          <div aria-hidden className={IMAGE_DARKEN_LAYER} />
          <div aria-hidden className={MEDIA_OVERLAY} />
          <div className="absolute inset-0 flex flex-col gap-m p-card-lg">
            <Eyebrow variant="eyebrow-sm" tone="inverse">
              {card.author} {card.handle}
            </Eyebrow>
            <Text
              variant="paragraph-md"
              as="blockquote"
              tone="inverse"
              className="sd:max-w-[360px]"
            >
              {card.quote}
            </Text>
          </div>
        </Card>
      );

    case "testimonial":
      if (card.poster === null) {
        return (
          <Card className="flex flex-col bg-surface-raised p-card-lg">
            <Eyebrow variant="eyebrow-sm" tone="secondary">
              {card.eyebrow}
            </Eyebrow>
            {card.quote !== null && (
              <Text
                variant="paragraph-md"
                as="blockquote"
                className="mt-m sd:max-w-[380px]"
              >
                “{card.quote}”
              </Text>
            )}
          </Card>
        );
      }

      return (
        <Card className="relative bg-surface-raised [aspect-ratio:5/6]">
          <Image
            src={card.poster}
            alt={card.posterAlt}
            fill
            sizes="(min-width: 1600px) 517px, (min-width: 1280px) 31vw, (min-width: 800px) 48vw, 100vw"
            className="object-cover"
          />
          <div aria-hidden className={IMAGE_DARKEN_LAYER} />
          <div aria-hidden className={MEDIA_OVERLAY} />
          <div className="absolute inset-0 flex flex-col p-card-lg">
            <Eyebrow variant="eyebrow-sm" tone="inverse">
              {card.eyebrow}
            </Eyebrow>
            {card.quote !== null && (
              <Text
                variant="paragraph-md"
                as="blockquote"
                tone="inverse"
                className="mt-m sd:max-w-[380px]"
              >
                “{card.quote}”
              </Text>
            )}
            {card.videoSrc !== null && (
              <IconButton
                label={`Play ${card.storySubject}'s story`}
                onClick={() => onPlay(card)}
                className="mt-auto"
              >
                <PlayIcon />
              </IconButton>
            )}
          </div>
        </Card>
      );

    case "stat":
      return <RotatingStatCard card={card} />;

    default:
      return assertNever(card);
  }
};

type RotatingStatCardProps = {
  card: Extract<ReviewWallCard, { kind: "stat" }>;
};

// The counter digits are Trim Mono (monospace) so the ticking number doesn't reflow.
const RotatingStatCard = ({ card }: RotatingStatCardProps) => {
  const [statIndex, setStatIndex] = useState(0);
  const statDescriptionId = useId();
  const announce = useAnnounce();
  const reducedMotion = usePrefersReducedMotion();
  const stat = card.stats[statIndex] ?? card.stats[0];
  const getStatSentence = (nextStat: (typeof card.stats)[number]) =>
    [nextStat.prefix, nextStat.srLabel, nextStat.unit, nextStat.suffix]
      .filter((part) => part !== "")
      .join(" ");

  const showNextStat = () => {
    const nextIndex = (statIndex + 1) % card.stats.length;
    const nextStat = card.stats[nextIndex] ?? card.stats[0];

    setStatIndex(nextIndex);
    announce(getStatSentence(nextStat));
  };

  return (
    <Card>
      <button
        type="button"
        aria-label="Show another stat"
        aria-describedby={statDescriptionId}
        onClick={showNextStat}
        className={cn(
          "group flex h-full w-full flex-col p-card-lg text-left",
          STAT_BUTTON_PRESS_CLASSES,
        )}
      >
        <VisuallyHidden id={statDescriptionId}>
          {getStatSentence(stat)}
        </VisuallyHidden>
        <Eyebrow variant="eyebrow-sm">Stats</Eyebrow>
        {/* Every stat stays mounted in one grid cell so the tallest one sizes
            the card — rotating can then never change the card's height. */}
        <div className="mb-l mt-l grid">
          {card.stats.map((layerStat, index) => (
            <motion.div
              key={layerStat.id}
              aria-hidden={index !== statIndex}
              initial={false}
              animate={
                index === statIndex
                  ? { opacity: 1, rotateX: 0 }
                  : { opacity: 0, rotateX: -80 }
              }
              transition={{
                duration: reducedMotion ? 0 : 0.35,
                ease: "easeOut",
              }}
              className={cn(
                "col-start-1 row-start-1",
                index !== statIndex && "pointer-events-none select-none",
              )}
            >
              <Text variant="h4" as="p" className="sd:max-w-[400px]">
                {layerStat.prefix}{" "}
                <span className="text-brand-orange">
                  <span className="whitespace-nowrap">
                    <span aria-hidden="true">{layerStat.valuePrefix}</span>
                    <LiveStat
                      seedValue={layerStat.seedValue}
                      perSecond={reducedMotion ? 0 : layerStat.perSecond}
                      seedTimeMs={layerStat.seedTimeMs}
                      srLabel={layerStat.srLabel}
                      isRevealed={!reducedMotion}
                      paused={reducedMotion || index !== statIndex}
                      className="font-trimMono tabular-nums"
                    />
                  </span>{" "}
                  {layerStat.unit}
                </span>{" "}
                {layerStat.suffix}
              </Text>
            </motion.div>
          ))}
        </div>
        <Eyebrow
          variant="eyebrow-sm"
          className="mt-auto w-fit rounded-full border border-tint-black-20 px-[12px] py-[6px] transition-colors duration-quick group-hover:border-content-primary group-hover:bg-brand-grey motion-reduce:transition-none"
        >
          ↻ {card.footnote}
        </Eyebrow>
      </button>
    </Card>
  );
};

const ReviewWallItemContent = ({
  item,
  onPlay,
}: {
  item: ReviewWallItem;
  onPlay: (card: ReviewWallTestimonialCard) => void;
}) =>
  item.source === "existing" ? (
    <WallCard card={item.card} onPlay={onPlay} />
  ) : (
    <PortedWallCard card={item.card} />
  );

const MasonryReviewCard = ({
  data: item,
}: RenderComponentProps<ReviewMasonryItem>) => (
  <AnimatePresence>
    <motion.div
      initial={item.reducedMotion ? false : { opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={
        item.reducedMotion
          ? { duration: 0 }
          : {
              opacity: MASONRY_ITEM_TRANSITION,
              scale: MASONRY_ITEM_TRANSITION,
            }
      }
      /* masonic already labels its positioning wrapper as the listitem. */
    >
      <ReviewWallItemContent item={item} onPlay={item.onPlay} />
    </motion.div>
  </AnimatePresence>
);

const useViewportSize = () => {
  const [viewportSize, setViewportSize] = useState<{
    width: number | undefined;
    height: number | undefined;
  }>({ width: undefined, height: undefined });

  useEffect(() => {
    const updateSize = () => {
      setViewportSize({
        width: Math.min(
          window.innerWidth,
          document.documentElement.clientWidth,
        ),
        height: Math.min(
          window.innerHeight,
          document.documentElement.clientHeight,
        ),
      });
    };

    updateSize();
    window.addEventListener("resize", updateSize);

    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  return viewportSize;
};

const useReviewPaging = (
  activeTabId: ReviewWallTabId,
  cards: readonly ReviewWallItem[],
) => {
  const [visibleCount, setVisibleCount] = useState(
    INITIAL_VISIBLE_REVIEW_COUNT,
  );
  /* The reset must happen in the SAME render as the tab switch (React's
     adjust-state-during-render pattern), never in an effect. An effect-based
     reset commits an intermediate render where the new tab's cards are
     sliced to the old tab's see-more-expanded count; the masonry measures
     those extra items under a positioner that only resets on tab id, and
     when the count then shrinks a render later, masonic maps its cached
     positions to indices past the new end of the array — itemKey receives
     undefined and the page crashes. */
  const [pagedTabId, setPagedTabId] = useState(activeTabId);
  if (pagedTabId !== activeTabId) {
    setPagedTabId(activeTabId);
    setVisibleCount(INITIAL_VISIBLE_REVIEW_COUNT);
  }
  const visibleCards = cards.slice(0, visibleCount);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((currentVisibleCount) =>
      Math.min(currentVisibleCount + LOAD_MORE_REVIEW_COUNT, cards.length),
    );
  }, [cards.length]);

  return {
    visibleCount,
    visibleCards,
    handleLoadMore,
  };
};

type ReviewMasonryLayoutArgs = {
  activeTabId: ReviewWallTabId;
  containerRef: Parameters<typeof useContainerPosition>[0];
  viewportWidth: number | undefined;
  viewportHeight: number | undefined;
};

const useReviewMasonryLayout = ({
  activeTabId,
  containerRef,
  viewportWidth,
  viewportHeight,
}: ReviewMasonryLayoutArgs) => {
  const { offset: containerTopOffset, width: measuredContainerWidth } =
    useContainerPosition(containerRef, [
      viewportWidth,
      viewportHeight,
      activeTabId,
    ]);
  const targetColumnCount =
    viewportWidth !== undefined && viewportWidth >= SMALL_DESKTOP_BREAKPOINT_PX
      ? 3
      : 2;
  const minimumLayoutWidth =
    MASONRY_COLUMN_GUTTER_PX * (targetColumnCount - 1) + targetColumnCount;
  const layoutWidth = Math.max(measuredContainerWidth, minimumLayoutWidth);
  const columnWidth = Math.max(
    (layoutWidth - MASONRY_COLUMN_GUTTER_PX * (targetColumnCount - 1)) /
      targetColumnCount,
    1,
  );
  const positioner = usePositioner(
    {
      width: layoutWidth,
      columnWidth,
      columnCount: targetColumnCount,
      columnGutter: MASONRY_COLUMN_GUTTER_PX,
      rowGutter: MASONRY_COLUMN_GUTTER_PX,
    },
    [activeTabId],
  );
  const resizeObserver = useResizeObserver(positioner);
  const hasMeasuredLayout =
    measuredContainerWidth > 0 && viewportHeight !== undefined;

  return {
    columnWidth,
    containerTopOffset,
    hasMeasuredLayout,
    positioner,
    resizeObserver,
  };
};

type MobileReviewScrollerProps = {
  activeTabId: ReviewWallTabId;
  cards: readonly ReviewWallItem[];
  onPlay: (card: ReviewWallTestimonialCard) => void;
  reducedMotion: boolean;
};

const MobileReviewScroller = ({
  activeTabId,
  cards,
  onPlay,
  reducedMotion,
}: MobileReviewScrollerProps) => {
  const cardStagger =
    (REVIEW_TRANSITION_DURATION_S - CARD_FADE_DURATION_S * 2) /
    Math.max(cards.length - 1, 1);
  const mobileGridVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reducedMotion ? 0 : cardStagger,
      },
    },
    exit: {},
  } satisfies Variants;

  return (
    <AnimatePresence initial={false} mode="wait">
      <motion.div
        key={activeTabId}
        variants={mobileGridVariants}
        initial={reducedMotion ? false : "hidden"}
        animate="visible"
        {...(reducedMotion ? {} : { exit: "exit" })}
        className="hideScrollBar flex w-full snap-x snap-mandatory gap-[18px] overflow-x-auto overflow-y-clip tab:gap-[16px] sd:gap-l"
      >
        {cards.map((item) => (
          <motion.div
            key={item.id}
            variants={REVIEW_CARD_VARIANTS}
            className="w-[calc(100%-63px)] shrink-0 snap-start tab:w-[475px] sd:w-[70%] sd:max-w-[306px]"
          >
            <ReviewWallItemContent item={item} onPlay={onPlay} />
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

type DesktopReviewMasonryProps = {
  activeTabId: ReviewWallTabId;
  cards: readonly ReviewWallItem[];
  hasMore: boolean;
  onLoadMore: () => void;
  onPlay: (card: ReviewWallTestimonialCard) => void;
  reducedMotion: boolean;
};

const DesktopReviewMasonry = ({
  activeTabId,
  cards,
  hasMore,
  onLoadMore,
  onPlay,
  reducedMotion,
}: DesktopReviewMasonryProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: viewportWidth, height: viewportHeight } = useViewportSize();
  const { containerTopOffset, hasMeasuredLayout, positioner, resizeObserver } =
    useReviewMasonryLayout({
      activeTabId,
      containerRef,
      viewportWidth,
      viewportHeight,
    });
  const masonryItems: ReviewMasonryItem[] = cards.map((item) => ({
    ...item,
    onPlay,
    reducedMotion,
  }));

  return (
    <div ref={containerRef}>
      {hasMeasuredLayout && (
        <>
          <MasonryScroller
            items={masonryItems}
            render={MasonryReviewCard}
            itemKey={getReviewWallItemKey}
            positioner={positioner}
            resizeObserver={resizeObserver}
            offset={containerTopOffset}
            height={viewportHeight ?? 0}
            role="list"
          />
          {hasMore && (
            <div className="mt-l flex justify-center">
              <Button variant="outline" onClick={onLoadMore}>
                {REVIEW_WALL.seeMoreLabel}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ReviewWallBody = ({
  sectionRef,
}: {
  sectionRef: RefObject<HTMLElement | null>;
}) => {
  const [activeTabId, setActiveTabId] = useState<ReviewWallTabId>(
    REVIEW_WALL.tabs[0].id,
  );
  const track = useTrack();
  const [playingCard, setPlayingCard] =
    useState<ReviewWallTestimonialCard | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const desktopMediaActive = useMediaQuery("(min-width: 800px)");
  const activeTab =
    REVIEW_WALL.tabs.find((tab) => tab.id === activeTabId) ??
    REVIEW_WALL.tabs[0];
  const existingCards: readonly ReviewWallCard[] =
    activeTabId === REVIEW_WALL.tabs[0].id
      ? [
          REVIEW_WALL.sharedCards.wired,
          REVIEW_WALL.sharedCards.rating,
          REVIEW_WALL.sharedCards.tweet,
          activeTab.testimonial,
          REVIEW_WALL.sharedCards.stat,
          REVIEW_WALL.sharedCards.verge,
        ]
      : [activeTab.testimonial];
  const cards: readonly ReviewWallItem[] = [
    ...existingCards.map((card) => ({
      id: card.id,
      source: "existing" as const,
      card,
    })),
    ...REVIEW_WALL_REVIEWS[activeTabId].map((card, index) => ({
      id: `${activeTabId}-ported-${index}`,
      source: "ported" as const,
      card,
    })),
  ];
  const { visibleCount, visibleCards, handleLoadMore } = useReviewPaging(
    activeTabId,
    cards,
  );

  const handleTabChange = (id: string) => {
    const tabIndex = REVIEW_WALL.tabs.findIndex((tab) => tab.id === id);
    const tab = REVIEW_WALL.tabs[tabIndex];
    if (tab === undefined) return;
    const nextTabId = tab.id;
    if (nextTabId === activeTabId) {
      return;
    }
    // Below the guard: re-pressing the active tab is a no-op, and counting it
    // inflated the tab totals with clicks that changed nothing.
    track("tab_selected", { tab: nextTabId, tab_index: tabIndex });

    const section = sectionRef.current;
    if (section !== null && section.getBoundingClientRect().top < 0) {
      section.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
      });
    }
    setActiveTabId(nextTabId);
  };

  return (
    <>
      {/* Sized to match the What's Inside heading at every width, which is
        the ramp both section headings on this page are meant to share —
        deliberately not the H1-then-H2 binding Figma 5413:17152 draws. Keep
        the two declarations identical; the sizes are load-bearing only in
        that they agree. */}
      <Text variant="h3" as="h2" className="text-center tab:text-h2 sd:text-h3">
        <span id="reviews-heading">{REVIEW_WALL.title}</span>
      </Text>

      <SegmentedTabs
        tabs={REVIEW_WALL.tabs.map(({ id, label }) => ({ id, label }))}
        activeId={activeTabId}
        onChange={handleTabChange}
        ariaLabel="Customer review categories"
        scrollable
        listWrapperClassName="mt-full flex max-w-full justify-center"
        panelClassName="relative z-0 mt-full w-full max-w-[1440px]"
        panel={
          desktopMediaActive === true ? (
            <DesktopReviewMasonry
              activeTabId={activeTabId}
              cards={visibleCards}
              hasMore={visibleCount < cards.length}
              onLoadMore={() => {
                handleLoadMore();
                // The count it expands to, not the one it expanded from.
                track("reviews_expanded", {
                  visible_count: Math.min(
                    visibleCount + LOAD_MORE_REVIEW_COUNT,
                    cards.length,
                  ),
                });
              }}
              onPlay={(card) => {
                setPlayingCard(card);
                track("video_played", { video_id: card.id });
              }}
              reducedMotion={reducedMotion}
            />
          ) : (
            <MobileReviewScroller
              activeTabId={activeTabId}
              cards={cards}
              onPlay={(card) => {
                setPlayingCard(card);
                track("video_played", { video_id: card.id });
              }}
              reducedMotion={reducedMotion}
            />
          )
        }
      />

      {playingCard !== null &&
        playingCard.poster !== null &&
        playingCard.videoSrc !== null && (
          <VideoPlayerModal
            videoSrc={playingCard.videoSrc}
            posterSrc={playingCard.poster.src}
            posterAlt={playingCard.posterAlt}
            onClose={() => setPlayingCard(null)}
          />
        )}
    </>
  );
};

const ReviewWall = ({ position }: { position: number }) => {
  // Separate so ReviewWallBody's `useTrack` sits below the section context
  // Section provides; the ref stays on the section for the tab scroll offset.
  const sectionRef = useRef<HTMLElement>(null);

  return (
    <Section
      id="reviews"
      position={position}
      ref={sectionRef}
      ariaLabelledBy="reviews-heading"
      className="flex scroll-mt-[calc(var(--spacing-button-height)+var(--spacing-space-half)+var(--spacing-space-quarter))] flex-col items-center overflow-x-clip px-grid-margin pb-page"
    >
      <ReviewWallBody sectionRef={sectionRef} />
    </Section>
  );
};

export default ReviewWall;
